import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import Button, { IconButton } from './Button';

/**
 * Dialog primitive.
 *
 * Below `sm` it is a full-screen sheet, because a centred dialog on a 375px
 * screen leaves the content in a box too small to use. Above `sm` it is a
 * centred, size-capped dialog.
 *
 * Handles the things hand-rolled overlays consistently forget: Escape to close,
 * background scroll lock, focus moved into the dialog and trapped there, and
 * focus returned to the trigger on close.
 */

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-4xl',
  xl: 'sm:max-w-6xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  closeOnBackdrop = true,
  children,
}) => {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      // Wrap focus at both ends so Tab can never escape the dialog.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocused.current = document.activeElement;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Defer so the panel exists before we reach into it.
    const raf = requestAnimationFrame(() => {
      const target = panelRef.current?.querySelector(FOCUSABLE) || panelRef.current;
      target?.focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-primary/60 backdrop-blur-sm animate-fade-in"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Dialog'}
        tabIndex={-1}
        className={[
          'relative flex max-h-[100dvh] w-full flex-col bg-surface shadow-lg outline-none',
          'h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:rounded-xl',
          'animate-slide-up sm:animate-fade-in',
          SIZES[size] || SIZES.md,
        ].join(' ')}
      >
        {(title || onClose) && (
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
            <div className="min-w-0">
              {title && <h2 className="truncate text-lg font-semibold text-foreground">{title}</h2>}
              {subtitle && <p className="mt-0.5 text-sm text-muted-fg">{subtitle}</p>}
            </div>
            <IconButton icon={CloseIcon} label="Close dialog" onClick={onClose} size="sm" />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          {children}
        </div>

        {footer && (
          // Column-reverse on mobile: actions stack full width (a column flex
          // container stretches its children) with the primary one on top,
          // instead of wrapping into a ragged two-row block. Inline and
          // right-aligned from `sm`.
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-muted/50 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:flex-row sm:items-center sm:justify-end sm:px-6 sm:pt-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/** Destructive-action confirmation. Replaces window.confirm across the app. */
export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size="sm"
    footer={
      <>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button variant={variant} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    <p className="text-sm leading-relaxed text-muted-fg">{message}</p>
  </Modal>
);

export default Modal;
